import React, { createContext } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TestCaseForm } from '@/components/testCase/TestCaseForm';
import { Section } from '@/components/Section';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { Button, Dialog } from 'alurkerja-ui';
import { useLocation, useParams } from 'react-router-dom';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { ITestCaseSchema, testCaseSchema, TestCaseService } from '@/api/testCase';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { Loading } from '@/components/Loading';
import { useBackNavigate } from '@/utils/hooks/useBackNavigate';
import { StepForm } from '@/components/step/StepForm';

type ICreateEditTestCaseContext = {
  step: number;
  setStep: React.Dispatch<number>;
};

export const CreateEditTestCaseContext =
  createContext<ICreateEditTestCaseContext>({
    step: 0,
    setStep: () => {
    },
  });

export const CreateEditTestCase: React.FC = () => {
  const { id, testCaseId } = useParams();

  const isClonePath = (pathname: string): boolean => {
    const match = pathname.match(/clone/);
    return match !== null;
  };

  const location = useLocation();
  const isClone = isClonePath(location.pathname);

  const backNavigate = useBackNavigate(isClone ? true : false);

  const { data: testCaseData, isFetching: isTestCaseFetching } =
    TestCaseService.useGet(testCaseId);
  const { data: optionsData, isFetching: isOptionsFetching } =
    TestCaseService.useGetTypeAndPriority(id);
  const { mutate: create, isPending: isCreateLoading } =
    TestCaseService.useCreate();
  const { mutate: update, isPending: isUpdateLoading } =
    TestCaseService.useUpdate();

  const isFetching = isTestCaseFetching || isOptionsFetching;
  const isLoading = isCreateLoading || isUpdateLoading;

  const form = useForm<ITestCaseSchema>({
    resolver: zodResolver(testCaseSchema),
    mode: 'onChange',
    values: {
      ...testCaseData,
      title: testCaseData?.original_title || '',
    } as ITestCaseSchema,
  });

  const submitAction = (data: ITestCaseSchema) => {
    data.project_id = id;

    if (testCaseId && !isClone) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Test case has been updated',
            callback: backNavigate,
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to update test case',
          });
        },
      });
    } else {
      const successDesc = isClone
        ? 'Test case has been duplicated'
        : 'Test case has been created';
      const failedDesc = isClone
        ? 'Failed to duplicate test case'
        : 'Failed to create test case';

      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: successDesc,
            callback: backNavigate,
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: failedDesc,
          });
        },
      });
    }
  };

  const backAction = backNavigate;

  const getTitle = (): string => {
    let title = 'Create Test Case';

    if (testCaseId) {
      title = isClone ? 'Clone Test Case' : 'Update Test Case';
    }

    return title;
  };

  const getButtonLabel = (): string => {
    let label = 'Create';

    if (testCaseId) {
      label = isClone ? 'Duplicate' : 'Update';
    }

    return label;
  };

  if (isFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <TabGroup className="tab-group">
      <TabList className="tab-list">
        <Tab className="tab-button">Test Case</Tab>
        <Tab className="tab-button">Step</Tab>
      </TabList>

      <FormContainer form={form} onSubmit={submitAction}>
        <Section title={getTitle()}>
          <TabPanels>
            <TabPanel>
              <TestCaseForm optionsData={optionsData} />
            </TabPanel>

            <TabPanel>
              <StepForm />
            </TabPanel>
          </TabPanels>
        </Section>

        <FormFooter className="tab-list">
          <OutlineButton onClick={backAction}>Back</OutlineButton>

          <Button loading={isLoading}>{getButtonLabel()}</Button>
        </FormFooter>
      </FormContainer>
    </TabGroup>
  );
};
